import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, MessageSquare, Brain, Layers, Send, FileText, Bell, Globe,
    Check, Zap, Sparkles, Users, ChevronDown
} from "lucide-react";
import { motion, useInView } from "framer-motion";
import CinematicNav from "@/components/CinematicNav";
import Footer from "@/components/Footer";
import ModuleAccent from "@/components/ModuleAccent";
import AIFlowDiagram from "@/components/AIFlowDiagram";
import { useAuth } from "@/contexts/AuthContext";

const EASE = [0.22, 1, 0.36, 1];

// ===== HERO =====
function Hero() {
    const { user } = useAuth();
    const isLogged = !!user && typeof user === "object";
    return (
        <section className="relative px-5 md:px-12 pt-32 pb-24 md:pt-44 md:pb-32" data-testid="totalpl-hero">
            <div className="max-w-6xl mx-auto text-center relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
                    style={{ background: "var(--mod-accent-soft)", border: "1px solid var(--mod-accent)" }}
                >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--mod-accent)", boxShadow: "0 0 8px var(--mod-accent-glow)" }} />
                    <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--mod-accent)", fontFamily: "JetBrains Mono, monospace" }}>
                        modulo · totalpl · printlab
                    </span>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 1.1, ease: EASE }}
                    className="tracking-tight text-white"
                    style={{
                        fontFamily: "Geist, Outfit, sans-serif",
                        fontSize: "clamp(2.4rem, 6vw, 5rem)",
                        lineHeight: 1.02,
                        letterSpacing: "-0.035em",
                        fontWeight: 200,
                    }}
                >
                    Ricevi ordini.<br />
                    <span style={{ fontWeight: 600 }}>Ti pensa l&apos;AI.</span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 1, ease: EASE }}
                    className="mt-6 text-white/65 mx-auto"
                    style={{ fontFamily: "Outfit, sans-serif", fontSize: "clamp(1.05rem, 1.8vw, 1.25rem)", lineHeight: 1.55, maxWidth: "32em" }}
                >
                    Il Sistema Operativo per la tua tipografia. WhatsApp e Telegram diventano commesse strutturate, la lavagna lavora da sola, i clienti sanno sempre a che punto è il loro ordine.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.55, duration: 1, ease: EASE }}
                    className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center"
                >
                    <Link
                        to={isLogged ? "/app" : "/signup"}
                        data-testid="totalpl-hero-cta-primary"
                        className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-white text-[14px] transition hover:scale-[1.03]"
                        style={{
                            background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))",
                            fontFamily: "Geist, sans-serif",
                            letterSpacing: "0.04em",
                            boxShadow: "0 18px 60px -10px var(--mod-accent-glow)",
                        }}
                    >
                        {isLogged ? "Apri TotalPL" : "Inizia gratis 30 giorni"} <ArrowRight size={16} />
                    </Link>
                    <a
                        href="#features"
                        data-testid="totalpl-hero-cta-secondary"
                        className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-white/85 hover:text-white text-[14px] border border-white/15 hover:bg-white/5 transition"
                        style={{ fontFamily: "Geist, sans-serif", letterSpacing: "0.04em" }}
                    >
                        Esplora le funzionalità
                    </a>
                </motion.div>
            </div>

            {/* Floating Telegram→AI demo */}
            <motion.div
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 1.1, ease: EASE }}
                className="mt-16 md:mt-24 max-w-3xl mx-auto px-4"
            >
                <PipelineDemo />
            </motion.div>
        </section>
    );
}

function PipelineDemo() {
    return (
        <div
            className="relative rounded-2xl overflow-hidden"
            style={{
                background: "rgba(15,18,36,0.65)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(20px) saturate(140%)",
                WebkitBackdropFilter: "blur(20px) saturate(140%)",
                boxShadow: "0 30px 80px -20px rgba(0,0,0,0.5)",
            }}
        >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
                <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(255,95,87,0.55)" }} />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(255,189,46,0.55)" }} />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "rgba(39,201,63,0.55)" }} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/30" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    pipeline · live demo
                </span>
            </div>
            <div className="grid md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-white/5">
                <Step
                    n="01"
                    title="WhatsApp / Telegram"
                    body={'"Mi servono 500 biglietti da visita per giovedì, urgenti"'}
                    icon={<MessageSquare size={14} />}
                />
                <Step
                    n="02"
                    title="AI parsa il messaggio"
                    body={'Cliente: ✓ · Reparto: digitale · Q: 500 · Deadline: gio · Priorità: urgente'}
                    icon={<Brain size={14} />}
                    accent
                />
                <Step
                    n="03"
                    title="Commessa pronta"
                    body={'Card creata in Kanban. Cliente riceve link tracking pubblico.'}
                    icon={<Check size={14} />}
                />
            </div>
        </div>
    );
}

function Step({ n, title, body, icon, accent }) {
    return (
        <div className="p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/45 mb-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                <span className={accent ? "" : "opacity-60"} style={accent ? { color: "var(--mod-accent)" } : null}>{icon}</span>
                <span>{n}</span>
            </div>
            <div className="text-white text-[14px] mb-1.5" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                {title}
            </div>
            <div className="text-white/55 text-[12.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                {body}
            </div>
        </div>
    );
}

// ===== PROBLEM SECTION =====
const PAINS = [
    { icon: MessageSquare, title: "WhatsApp infinito", body: "Riceves ordini in chat, scrolli, perdi, dimentichi. Niente storico." },
    { icon: FileText, title: "Excel + post-it", body: "Scrivi tutto a mano. Quando arriva il dipendente nuovo, niente è documentato." },
    { icon: Bell, title: '"È pronto?"', body: "I clienti chiamano ogni ora. Tu vai in officina a chiedere. Tempo bruciato." },
];

function PainSection() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    return (
        <section ref={ref} className="relative px-5 md:px-12 py-24 md:py-32" data-testid="totalpl-pain">
            <div className="max-w-5xl mx-auto">
                <div className="text-center mb-14">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, ease: EASE }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                    >
                        <span className="text-[10px] uppercase tracking-[0.22em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            il problema
                        </span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.1, duration: 1, ease: EASE }}
                        className="text-white tracking-tight"
                        style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(1.9rem, 4.4vw, 3.6rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
                    >
                        Una tipografia oggi <span style={{ fontWeight: 600 }}>perde il 40%</span><br />
                        del tempo solo a gestire.
                    </motion.h2>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                    {PAINS.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.15 + i * 0.1, duration: 0.9, ease: EASE }}
                            className="p-6 rounded-2xl"
                            style={{
                                background: "rgba(15,18,36,0.5)",
                                border: "1px solid rgba(255,255,255,0.07)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                            }}
                        >
                            <p.icon size={20} className="text-white/40 mb-4" />
                            <div className="text-white text-[16px] mb-2" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                                {p.title}
                            </div>
                            <p className="text-white/55 text-[13.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                                {p.body}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ===== FEATURES =====
const FEATURES = [
    { icon: Brain, title: "AI parsing italiano", body: 'Capisce "giovedì", "fra 3 giorni", "asap". Crea la commessa già strutturata.', tag: "core" },
    { icon: MessageSquare, title: "Telegram multi-operatore", body: "Ogni dipendente ha il suo accesso. Bot unico, mapping per tenant.", tag: "live" },
    { icon: Layers, title: "Kanban personalizzabile", body: "5 stati base + custom. Drag&drop. Cambio stato notifica il cliente.", tag: "live" },
    { icon: Globe, title: "Landing tipografia", body: "Mini-sito pubblico /p/<tua-slug> con widget, QR code, form ordine.", tag: "live" },
    { icon: Send, title: "Tracking pubblico cliente", body: "Link univoco /track/<token>. Il cliente vede stato senza login.", tag: "live" },
    { icon: FileText, title: "Storage BYO Drive", body: "I file dei clienti vanno nel tuo Google Drive. Privacy + costo zero.", tag: "live" },
];

function FeaturesSection() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    return (
        <section id="features" ref={ref} className="relative px-5 md:px-12 py-24 md:py-32" data-testid="totalpl-features">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-14">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, ease: EASE }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                        style={{ background: "var(--mod-accent-soft)", border: "1px solid var(--mod-accent)" }}
                    >
                        <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--mod-accent)", fontFamily: "JetBrains Mono, monospace" }}>
                            6 funzionalità · 1 sistema
                        </span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.1, duration: 1, ease: EASE }}
                        className="text-white tracking-tight"
                        style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.6vw, 4rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
                    >
                        Un Sistema Operativo. Per la tua <span style={{ fontWeight: 600 }}>tipografia</span>.
                    </motion.h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    {FEATURES.map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.1 + i * 0.07, duration: 0.85, ease: EASE }}
                            whileHover={{ y: -4 }}
                            className="group relative rounded-2xl overflow-hidden"
                            style={{
                                background: "rgba(15,18,36,0.55)",
                                border: "1px solid rgba(255,255,255,0.07)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                transition: "all 400ms cubic-bezier(0.16,1,0.3,1)",
                            }}
                        >
                            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
                                <div className="flex gap-1.5">
                                    <span className="w-2 h-2 rounded-full" style={{ background: "rgba(255,95,87,0.55)" }} />
                                    <span className="w-2 h-2 rounded-full" style={{ background: "rgba(255,189,46,0.55)" }} />
                                    <span className="w-2 h-2 rounded-full" style={{ background: "rgba(39,201,63,0.55)" }} />
                                </div>
                                <span className="text-[9.5px] uppercase tracking-[0.18em] text-white/30" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                    {f.tag}
                                </span>
                            </div>
                            <div className="p-6">
                                <f.icon size={22} className="mb-4" style={{ color: "var(--mod-accent)" }} />
                                <div className="text-white text-[16px] mb-2" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                                    {f.title}
                                </div>
                                <p className="text-white/55 text-[13px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                                    {f.body}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ===== AI FLOW SCENE =====
function AIFlowScene() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    return (
        <section ref={ref} className="relative px-5 md:px-12 py-24 md:py-32" data-testid="totalpl-aiflow">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-14">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, ease: EASE }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                    >
                        <span className="text-[10px] uppercase tracking-[0.22em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            il cervello al centro
                        </span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.1, duration: 1, ease: EASE }}
                        className="text-white tracking-tight"
                        style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.6vw, 4rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
                    >
                        4 canali in entrata.<br />
                        <span style={{ fontWeight: 600 }}>5 azioni automatiche</span> in uscita.
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.2, duration: 1, ease: EASE }}
                        className="mt-5 text-white/55 mx-auto"
                        style={{ fontFamily: "Outfit, sans-serif", fontSize: "clamp(0.95rem, 1.5vw, 1.1rem)", lineHeight: 1.55, maxWidth: "36em" }}
                    >
                        WhatsApp, Email, Telegram, Drive entrano. L&apos;AI legge, capisce, organizza. La tua tipografia non aspetta più nessuno.
                    </motion.p>
                </div>
                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.3, duration: 1.1, ease: EASE }}
                    className="max-w-4xl mx-auto"
                >
                    <AIFlowDiagram />
                </motion.div>
            </div>
        </section>
    );
}

// ===== PRICING =====
const PLANS = [
    { name: "Starter", price: "19", brief: "Per provare e tipografie 1-uomo", features: ["1 operatore", "50 job/mese", "Storage manuale", "50 parsing AI/mese", "Email support"] },
    { name: "Pro", price: "49", brief: "La scelta consigliata", popular: true, features: ["3 operatori", "200 job/mese", "Google Drive BYO", "500 parsing AI/mese", "Landing tipografia", "Telegram bot"] },
    { name: "Studio", price: "99", brief: "Tipografie 5-10 persone", features: ["10 operatori", "Job illimitati", "Google Drive BYO", "AI parsing illimitato", "Landing + dominio custom", "Telegram bot", "Priority support"] },
    { name: "Enterprise", price: "custom", brief: "Multi-sede o requisiti speciali", features: ["Operatori illimitati", "Storage proprio", "SSO custom", "SLA dedicato", "Onboarding 1-to-1"] },
];

function PricingSection() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });
    const { user } = useAuth();
    const isLogged = !!user && typeof user === "object";
    return (
        <section ref={ref} className="relative px-5 md:px-12 py-24 md:py-32" data-testid="totalpl-pricing">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-14">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, ease: EASE }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                    >
                        <span className="text-[10px] uppercase tracking-[0.22em] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            piani · 30 giorni gratis
                        </span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.1, duration: 1, ease: EASE }}
                        className="text-white tracking-tight"
                        style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.6vw, 4rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
                    >
                        Paghi solo se ti <span style={{ fontWeight: 600 }}>cambia la vita</span>.
                    </motion.h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                    {PLANS.map((p, i) => (
                        <motion.div
                            key={p.name}
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ delay: 0.1 + i * 0.08, duration: 0.9, ease: EASE }}
                            className="relative p-6 rounded-2xl flex flex-col"
                            style={{
                                background: p.popular ? "linear-gradient(180deg, var(--mod-accent-soft) 0%, rgba(15,18,36,0.55) 100%)" : "rgba(15,18,36,0.55)",
                                border: p.popular ? "1px solid var(--mod-accent)" : "1px solid rgba(255,255,255,0.07)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                boxShadow: p.popular ? "0 24px 60px -20px var(--mod-accent-glow)" : "none",
                            }}
                        >
                            {p.popular && (
                                <div
                                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] text-white"
                                    style={{ background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))", fontFamily: "JetBrains Mono, monospace" }}
                                >
                                    consigliato
                                </div>
                            )}
                            <div className="text-white text-[18px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                                {p.name}
                            </div>
                            <div className="text-white/45 text-[12.5px] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                                {p.brief}
                            </div>
                            <div className="flex items-baseline gap-1 my-5">
                                <span className="text-white" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "44px", letterSpacing: "-0.03em", lineHeight: 1 }}>
                                    {p.price === "custom" ? "Custom" : `€${p.price}`}
                                </span>
                                {p.price !== "custom" && <span className="text-white/45 text-[13px]">/mese</span>}
                            </div>
                            <ul className="space-y-2 text-[12.5px] text-white/70 mb-6 flex-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2">
                                        <Check size={13} className="mt-0.5 flex-none" style={{ color: "var(--mod-accent)" }} />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to={isLogged ? "/app" : "/signup"}
                                data-testid={`totalpl-plan-cta-${p.name.toLowerCase()}`}
                                className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[13px] transition"
                                style={p.popular ? {
                                    background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))",
                                    color: "#fff",
                                    fontFamily: "Geist, sans-serif",
                                    letterSpacing: "0.02em",
                                    boxShadow: "0 10px 30px -8px var(--mod-accent-glow)",
                                } : {
                                    border: "1px solid rgba(255,255,255,0.15)",
                                    color: "#fff",
                                    fontFamily: "Geist, sans-serif",
                                    letterSpacing: "0.02em",
                                }}
                            >
                                {p.price === "custom" ? "Contattaci" : "Inizia 30gg gratis"} <ArrowRight size={13} />
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ===== FAQ =====
const FAQ = [
    { q: "Quanto ci metto a partire?", a: "10 minuti. Crei l'account, importi i tuoi reparti, attivi il bot Telegram, inviti i dipendenti. Il primo ordine può entrare oggi stesso." },
    { q: "I miei dati sono al sicuro?", a: "Sì. Database in Europa, backup automatici, GDPR-compliant. I file dei tuoi clienti possono stare sul tuo Google Drive (privacy massima)." },
    { q: "Devo per forza usare Telegram?", a: "No. Puoi creare commesse manualmente, ricevere ordini dal tuo mini-sito pubblico, o ricevere via email. Telegram è solo il canale più veloce." },
    { q: "Posso annullare quando voglio?", a: "Sì, in qualsiasi momento. Niente vincoli annuali. I tuoi dati restano scaricabili per 30 giorni dopo la disdetta." },
    { q: "Funziona anche se non sono pratico di tecnologia?", a: 'È pensata per chi "non ha tempo per imparare un software". Onboarding 1-to-1 guidato compreso nel piano Pro e superiori.' },
];

function FAQSection() {
    const [open, setOpen] = useState(0);
    return (
        <section className="relative px-5 md:px-12 py-24 md:py-32" data-testid="totalpl-faq">
            <div className="max-w-3xl mx-auto">
                <div className="text-center mb-12">
                    <h2
                        className="text-white tracking-tight"
                        style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.4vw, 3.6rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
                    >
                        Domande <span style={{ fontWeight: 600 }}>frequenti</span>.
                    </h2>
                </div>
                <div className="space-y-2">
                    {FAQ.map((f, i) => {
                        const isOpen = open === i;
                        return (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setOpen(isOpen ? -1 : i)}
                                className="w-full text-left rounded-xl px-5 py-4 transition"
                                style={{
                                    background: isOpen ? "var(--mod-accent-soft)" : "rgba(15,18,36,0.5)",
                                    border: `1px solid ${isOpen ? "var(--mod-accent)" : "rgba(255,255,255,0.07)"}`,
                                }}
                                data-testid={`totalpl-faq-${i}`}
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <span className="text-white text-[14.5px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                                        {f.q}
                                    </span>
                                    <ChevronDown size={16} className="text-white/45 flex-none" style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                                </div>
                                {isOpen && (
                                    <p className="mt-3 text-white/65 text-[13.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                                        {f.a}
                                    </p>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ===== FINAL CTA =====
function FinalCTA() {
    const { user } = useAuth();
    const isLogged = !!user && typeof user === "object";
    return (
        <section className="relative px-5 md:px-12 py-24 md:py-32 text-center" data-testid="totalpl-final-cta">
            <div className="max-w-3xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: EASE }}
                >
                    <Zap size={28} className="mx-auto mb-5" style={{ color: "var(--mod-accent)" }} />
                    <h2
                        className="text-white tracking-tight mb-6"
                        style={{ fontFamily: "Geist, Outfit, sans-serif", fontWeight: 200, fontSize: "clamp(2.2rem, 5.4vw, 4.4rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
                    >
                        Pronto a smettere di rincorrere <span style={{ fontWeight: 600 }}>gli ordini</span>?
                    </h2>
                    <p className="text-white/65 text-[15px] max-w-xl mx-auto mb-9" style={{ fontFamily: "Outfit, sans-serif", lineHeight: 1.55 }}>
                        30 giorni gratis. Nessuna carta richiesta. Annulli quando vuoi.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                        <Link
                            to={isLogged ? "/app" : "/signup"}
                            data-testid="totalpl-final-cta-primary"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white text-[15px] transition hover:scale-[1.03]"
                            style={{
                                background: "linear-gradient(135deg, var(--mod-accent), var(--mod-accent-deep))",
                                fontFamily: "Geist, sans-serif",
                                letterSpacing: "0.04em",
                                boxShadow: "0 20px 70px -10px var(--mod-accent-glow)",
                            }}
                        >
                            {isLogged ? "Apri TotalPL" : "Inizia gratis"} <ArrowRight size={16} />
                        </Link>
                        <button
                            type="button"
                            onClick={() => window.dispatchEvent(new CustomEvent("open-aria"))}
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white text-[15px] border border-white/15 hover:bg-white/5 transition"
                            style={{ fontFamily: "Geist, sans-serif", letterSpacing: "0.04em" }}
                            data-testid="totalpl-final-cta-aria"
                        >
                            <Sparkles size={15} /> Parla con Aria
                        </button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

// ===== MAIN =====
export default function MarketingHome() {
    useEffect(() => {
        document.title = "TotalPL — Il Sistema Operativo per la tua tipografia";
    }, []);

    return (
        <ModuleAccent
            module="totalpl"
            as="div"
            className="min-h-screen w-full overflow-x-hidden"
            style={{
                background: "#05050F",
                color: "#fff",
                fontFamily: "Geist, Outfit, sans-serif",
            }}
            data-testid="totalpl-landing"
        >
            <CinematicNav />
            <Hero />
            <PainSection />
            <FeaturesSection />
            <AIFlowScene />
            <PricingSection />
            <FAQSection />
            <FinalCTA />
            <Footer variant="hub" />
        </ModuleAccent>
    );
}
